import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Card from './Card'
import { COLORS, PAGE_BREAKPOINTS } from 'parameters/index'
import { getEnv } from 'lib/envWrapper'
import { validateEmail, validatePassword } from 'lib'
import Logger from 'services/Logger'
import { getUserClient } from 'services/FionaAPI/clients'
import { clearSession } from 'services/Authentication/keycloak/session'
import { Modal, Overlay } from 'components/elements/modals/ConfirmModal'
import { Label } from 'ui/inputs/fields/Label'
import { TextInput } from 'components/inputs'
import { Heading } from 'components/styles'
import { Box, Stack } from 'components/layout/primitives'
import { UserWithRelations } from 'generated/api'
import { Controller, useForm } from 'react-hook-form'
import { ConfirmModal } from 'components/layout'
import { Button } from 'ui/inputs/Button/LegacyButtonCompatibility'
// TODO: Switch All buttons
const env = getEnv()

interface Props {
	profile: UserWithRelations
	setProfile: (profile: UserWithRelations) => void
}

interface FormData {
	firstName: string
	lastName: string
	email: string
}

const YourInformation = ({ profile, setProfile }: Props): JSX.Element => {
	const { setValue, handleSubmit, watch, control } = useForm<FormData>()

	const [state, setState] = React.useState({
		isEdit: false,
		isEmailValid: false,
		oldPassword: {
			value: '',
			isVisible: false
		},
		newPassword: {
			value: '',
			isVisible: false
		},
		passwordValidation: {
			isLengthValid: false,
			hasUppercase: false,
			hasNumber: false
		}
	})
	const [nameModal, triggerNameModal] = useState<boolean>(false)
	const [emailModal, triggerEmailModal] = useState<boolean>(false)

	function truncate(str?: string, n?: number) {
		if (str && n) return str.length > n ? str.slice(0, n - 1) : str
	}

	const toggleEditForm = (): void => {
		setState(prevState => ({
			...prevState,
			isEdit: !prevState.isEdit
		}))
	}

	async function deactivate(): Promise<void> {
		try {
			const userClient = getUserClient()
			await userClient.disableUser()
			clearSession('/account/deactivate')
		} catch (err) {
			Logger.error(err)
			clearSession('/account/deactivate')
		}
	}

	useEffect(() => {
		const value = validatePassword(state.newPassword.value)

		setState(prevState => ({
			...prevState,
			passwordValidation: value
		}))
	}, [state.newPassword.value])

	useEffect(() => {
		const value = validateEmail(profile?.email || '').isValid

		setState(prevState => ({
			...prevState,
			isEmailValid: value
		}))
	}, [profile?.email])

	const reset = (): void => {
		window.location.href = `${env.keycloak.host}/realms/fiona/login-actions/reset-credentials`
	}

	const handleNameForm = async (data: FormData): Promise<void> => {
		triggerNameModal(false)
		if (watch('firstName') == '' || watch('lastName') == '') return
		try {
			const userClient = getUserClient()
			if (profile.id) {
				await userClient.editUser({
					id: profile?.id!,
					userWithoutId: { firstName: truncate(data.firstName, 50), lastName: truncate(data.lastName, 50) }
				})
			}
		} catch (err) {
			Logger.error(err)
		}

		try {
			const client = getUserClient()

			const user = await client.getUser({ id: profile.id })
			if (user.firstName && user.lastName) {
				setProfile({ ...profile, lastName: user.lastName, firstName: user.firstName })
			}
		} catch (err) {
			Logger.error(err)
		}
	}

	const handleEmailForm = async (data: FormData): Promise<void> => {
		triggerEmailModal(false)
		if (watch('email') == '') return
		try {
			const userClient = getUserClient()
			if (profile.id) {
				await userClient.editUser({ id: profile.id, userWithoutId: { email: truncate(data.email, 50) } })
			}
		} catch (err) {
			Logger.error(err)
		}

		try {
			const client = getUserClient()
			const user = await client.getUser({ id: profile.id })
			if (user.email) {
				setProfile({ ...profile, email: user.email })
			}
		} catch (err) {
			Logger.error(err)
		}
	}

	const handleNameModal = () => {
		if (profile.firstName) setValue('firstName', profile.firstName)
		if (profile.lastName) setValue('lastName', profile.lastName)
		triggerNameModal(true)
	}

	const handleEmailModal = () => {
		if (profile.email) setValue('email', profile.email)
		triggerEmailModal(true)
	}

	return (
		<Card title="Your Information" onActionClick={toggleEditForm} actionButtonText="" style={{ height: '100%' }}>
			<YourInformationWrapper>
				<YourInformationContent>
					<div>
						<DisabledInput>
							<DisabledInputLabel>First Name</DisabledInputLabel>
							<DisabledInputText>{truncate(profile.firstName, 50)}</DisabledInputText>
						</DisabledInput>
						<DisabledInput>
							<DisabledInputLabel>Last Name</DisabledInputLabel>
							<DisabledInputText>{truncate(profile.lastName, 50)}</DisabledInputText>
						</DisabledInput>
						<DisabledInput>
							<DisabledInputLabel>Email Address</DisabledInputLabel>
							<DisabledInputText>{truncate(profile.email, 50)}</DisabledInputText>
						</DisabledInput>
						<DisabledInput>
							<DisabledInputLabel>Password</DisabledInputLabel>
							<DisabledInputText>************</DisabledInputText>
						</DisabledInput>
					</div>
				</YourInformationContent>
				<Actions>
					<ActionTextWrapper>
						<ActionText isActive={true} onClick={handleNameModal}>
							Change Name
						</ActionText>
						<ActionText isActive={true} onClick={handleEmailModal}>
							Change Email
						</ActionText>
						<ActionText onClick={reset} isActive={true}>
							Reset Password
						</ActionText>
					</ActionTextWrapper>
					<Br />
					<ConfirmModal
						trigger={<ActionText isActive>Deactivate your account</ActionText>}
						title="Are you sure you want to deactivate your account and it's history? This cannot be undone."
						onConfirm={deactivate}
						confirmText="Deactivate"
					/>
				</Actions>
				{nameModal && (
					<Overlay>
						<Modal>
							<Box space="0">
								<Stack space="big">
									<Heading.H4>Enter your name</Heading.H4>
									<Stack space="base">
										<Label htmlFor="form-firstName" label="First Name" />
										<Controller
											name="firstName"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextInput type="text" id="form-firstName" value={value} onChange={onChange} />
											)}
										/>
									</Stack>
									<Stack space="base">
										<Label htmlFor="form-lastName" label="Last Name" />
										<Controller
											name="lastName"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextInput type="text" id="form-lastName" value={value} onChange={onChange} />
											)}
										/>
									</Stack>
									<Stack modifiers={{ flexDirection: 'row', alignItems: 'center' }}>
										<Button variant="ghost" onClick={(): void => triggerNameModal(false)}>
											Cancel
										</Button>
										<Button onClick={handleSubmit(handleNameForm)}>Save Changes</Button>
									</Stack>
								</Stack>
							</Box>
						</Modal>
					</Overlay>
				)}
				{emailModal && (
					<Overlay>
						<Modal>
							<Box space="0">
								<Stack space="big">
									<Stack space="base">
										<Label htmlFor="form-email" label="Email" />
										<Controller
											name="email"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextInput type="email" id="form-email" value={value} onChange={onChange} />
											)}
										/>
									</Stack>
									<Stack modifiers={{ flexDirection: 'row', alignItems: 'center' }}>
										<Button variant="ghost" onClick={(): void => triggerEmailModal(false)}>
											Cancel
										</Button>
										<Button onClick={handleSubmit(handleEmailForm)}>Save Changes</Button>
									</Stack>
								</Stack>
							</Box>
						</Modal>
					</Overlay>
				)}
			</YourInformationWrapper>
		</Card>
	)
}

export default YourInformation

const YourInformationWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}
`

const ActionText = styled.span<{ isActive?: boolean }>`
	font-size: 16px;
	font-weight: 600;
	padding-right: 16px;
	cursor: pointer;
	color: ${(props): string => (props.isActive ? `${COLORS.KSU_PURPLE}` : `${COLORS.OTHER_GRAY}`)};
	pointer-events: ${(props): string => (props.isActive ? 'auto' : 'none')};
`

const ActionTextWrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
`

const Actions = styled.div`
	width: 100%;
`

const Br = styled.div`
	width: 100%;
	height: 1px;
	border-radius: 2px;
	background-color: ${COLORS.OTHER_GRAY};
	margin: 24px 0;
`

const YourInformationContent = styled.div`
	flex: 1;
	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		width: 100%;
	}
`

// const ProfilePicture = styled.img`
// 	width: 120px;
// 	height: 120px;
// 	border-radius: 100%;
// 	margin-right: 0;

// 	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
// 		margin-right: 0;
// 		margin-bottom: 20px;
// 		order: -1;
// 	}
// `

const DisabledInput = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 24px;
`

const DisabledInputLabel = styled.span`
	font-size: 11px;
	color: ${COLORS.SPANISH_GRAY};
	margin-bottom: 8px;
`

const DisabledInputText = styled.span`
	font-size: 16px;
`

// const List = styled.ul`
// 	list-style-type: none;
// 	padding-left: 0;
// `

// const ListItem = styled.li`
// 	display: flex;
// 	align-items: center;
// 	font-size: 14px;
// 	color: ${COLORS.SPANISH_GRAY};
// 	margin-bottom: 8px;
// `
